import { calculateAu13 } from "./calculate.js";
import { fy2023_24_postReform, fy2023_24_preReform } from "./fy2023-24.js";
import { fy2024_25 } from "./fy2024-25.js";
import { fy2025_26 } from "./fy2025-26.js";
import type { Au13Input, Au13Output, Au13RuleSet } from "./types.js";

/**
 * Backdated-reclaim resolution for AU-13. The CCS reclaim hook (spec §7.2) lets
 * a family re-run a PRIOR financial year's care against the rules in force
 * THEN — this is the immutability requirement made concrete: we never run an
 * old reclaim against today's thresholds.
 *
 * FY2023-24 is special: the Cheaper Child Care reform split it mid-year (from
 * 10 Jul 2023), so resolving FY2023-24 requires the specific care date to pick
 * the pre- or post-reform period. All other years map to a single period.
 */

const FY2023_24_REFORM_DATE = "2023-07-10";

/** All AU-13 periods, indexed by the [from, to] dates they govern. */
const PERIODS: readonly Au13RuleSet[] = [
  fy2023_24_preReform,
  fy2023_24_postReform,
  fy2024_25,
  fy2025_26,
];

/**
 * Pick the AU-13 rule period whose `[periodFrom, periodTo]` window contains an
 * ISO care date (YYYY-MM-DD). Pure string comparison — ISO dates sort
 * lexically. Throws if the date predates the earliest registered period.
 */
export function au13PeriodForDate(careDate: string): Au13RuleSet {
  for (const p of PERIODS) {
    if (careDate >= p.periodFrom && careDate <= p.periodTo) return p;
  }
  throw new Error(`no AU-13 rule period registered for care date ${careDate}`);
}

export interface Au13ReclaimResult {
  /** The period the reclaim resolved against (for audit + copy). */
  periodKey: string;
  periodLabel: string;
  /** The care date that selected the period. */
  careDate: string;
  /** The financial year this period belongs to. */
  fy: string;
  /** The calculation against THAT period's rules. */
  output: Au13Output;
  /** True when the care date fell on/after the 10 Jul 2023 reform cut-over. */
  postCheaperChildCareReform: boolean;
}

/**
 * Resolve a backdated CCS reclaim: given a past care date and the family's
 * inputs as they were then, compute the entitlement against the rules in force
 * on that date. Demonstrates historical resolution / ruleset immutability.
 */
export function resolveAu13Reclaim(
  careDate: string,
  input: Au13Input,
): Au13ReclaimResult {
  const period = au13PeriodForDate(careDate);
  return {
    periodKey: period.periodKey,
    periodLabel: period.periodLabel,
    careDate,
    fy: period.fy,
    output: calculateAu13(input, period),
    postCheaperChildCareReform: careDate >= FY2023_24_REFORM_DATE,
  };
}
